<template>
  <div class="cc-tool">
    <div class="editor-pane">
      <div class="pane-header">
        <span class="pane-dot input-dot" /><span class="pane-label">{{ $t('tool.CoordConverter.title') }}</span>
        <span class="pane-hint">GCJ-02 · WGS-84 · BD-09 互转</span>
      </div>

      <el-tabs v-model="tab" type="border-card" class="cc-tabs">
        <el-tab-pane label="单点转换" name="single">
          <div class="cc-form">
            <div class="cc-field">
              <label>经度</label>
              <el-input v-model.number="lng" type="number" size="small" placeholder="如 116.397428" @input="convertSingle" />
            </div>
            <div class="cc-field">
              <label>纬度</label>
              <el-input v-model.number="lat" type="number" size="small" placeholder="如 39.90923" @input="convertSingle" />
            </div>
            <div class="cc-field">
              <label>源坐标系</label>
              <el-select v-model="fromSys" size="small" @change="convertSingle">
                <el-option label="WGS-84 (GPS)" value="wgs84" />
                <el-option label="GCJ-02 (高德/腾讯)" value="gcj02" />
                <el-option label="BD-09 (百度)" value="bd09" />
              </el-select>
            </div>
            <div class="cc-field">
              <label>目标坐标系</label>
              <el-select v-model="toSys" size="small" @change="convertSingle">
                <el-option label="WGS-84 (GPS)" value="wgs84" />
                <el-option label="GCJ-02 (高德/腾讯)" value="gcj02" />
                <el-option label="BD-09 (百度)" value="bd09" />
              </el-select>
            </div>
          </div>
        </el-tab-pane>
        <el-tab-pane label="批量转换" name="batch">
          <div class="cc-batch-hint">每行一组坐标：经度,纬度</div>
          <el-input v-model="batchInput" type="textarea" :rows="6" size="small" placeholder="116.397428,39.90923&#10;121.473701,31.230416&#10;104.06654,30.57227" @input="convertBatch" />
          <el-button size="small" style="margin-top:8px;" plain @click="clearBatch">{{ $t("common.action.clear") }}</el-button>
        </el-tab-pane>
      </el-tabs>
    </div>

    <!-- Single result -->
    <div class="cc-result" v-if="tab === 'single' && singleResult">
      <div class="cc-result-bar">转换结果</div>
      <table class="cc-table">
        <thead><tr><th>坐标系</th><th>经度</th><th>纬度</th><th>说明</th></tr></thead>
        <tbody>
          <tr :class="{ 'cc-highlight': sys === toSys }" v-for="(item, sys) in singleResult" :key="sys">
            <td><span class="cc-tag" :class="'cc-tag-'+sys">{{ sysLabels[sys] }}</span></td>
            <td><code>{{ item.lng.toFixed(6) }}</code></td>
            <td><code>{{ item.lat.toFixed(6) }}</code></td>
            <td class="cc-desc">{{ sysDescs[sys] }}</td>
          </tr>
        </tbody>
      </table>
      <div class="cc-action">
        <el-button size="small" plain icon="el-icon-document-copy" @click="copySingle">复制结果</el-button>
      </div>
    </div>

    <!-- Batch result -->
    <div class="cc-result" v-if="tab === 'batch' && batchResult.length">
      <div class="cc-result-bar">转换结果（{{ fromLabel }} → {{ toLabel }}）</div>
      <table class="cc-table">
        <thead><tr><th>#</th><th>原始经度</th><th>原始纬度</th><th>转换后经度</th><th>转换后纬度</th></tr></thead>
        <tbody>
          <tr v-for="(r, i) in batchResult" :key="i">
            <td>{{ i + 1 }}</td>
            <td><code>{{ r.fromLng }}</code></td>
            <td><code>{{ r.fromLat }}</code></td>
            <td><code>{{ r.toLng.toFixed(6) }}</code></td>
            <td><code>{{ r.toLat.toFixed(6) }}</code></td>
          </tr>
        </tbody>
      </table>
      <div class="cc-action">
        <el-button size="small" plain icon="el-icon-document-copy" @click="copyBatch">复制结果</el-button>
      </div>
    </div>

    <!-- Coordinate reference -->
    <div class="cc-ref">
      <div class="cc-ref-title">坐标系说明</div>
      <div class="cc-ref-grid">
        <div class="cc-ref-item">
          <div class="cc-ref-name">WGS-84</div>
          <div class="cc-ref-text">国际标准坐标系 (EPSG:4326)，GPS 和 Google Earth 使用</div>
        </div>
        <div class="cc-ref-item">
          <div class="cc-ref-name">GCJ-02</div>
          <div class="cc-ref-text">国测局（火星）坐标系，高德地图、腾讯地图使用</div>
        </div>
        <div class="cc-ref-item">
          <div class="cc-ref-name">BD-09</div>
          <div class="cc-ref-text">百度坐标系，在 GCJ-02 基础上二次加密</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
var PI = Math.PI
var X_PI = PI * 3000.0 / 180.0
var A = 6378245.0
var EE = 0.00669342162296594323

function outOfChina(lng, lat) {
  return lng < 72.004 || lng > 137.8347 || lat < 0.8293 || lat > 55.8271
}

function transformLng(x, y) {
  var ret = 300.0 + x + 2.0 * y + 0.1 * x * x + 0.1 * x * y + 0.1 * Math.sqrt(Math.abs(x))
  ret += (20.0 * Math.sin(6.0 * x * PI) + 20.0 * Math.sin(2.0 * x * PI)) * 2.0 / 3.0
  ret += (20.0 * Math.sin(x * PI) + 40.0 * Math.sin(x / 3.0 * PI)) * 2.0 / 3.0
  ret += (150.0 * Math.sin(x / 12.0 * PI) + 300.0 * Math.sin(x / 30.0 * PI)) * 2.0 / 3.0
  return ret
}

function transformLat(x, y) {
  var ret = -100.0 + 2.0 * x + 3.0 * y + 0.2 * y * y + 0.1 * x * y + 0.2 * Math.sqrt(Math.abs(x))
  ret += (20.0 * Math.sin(6.0 * x * PI) + 20.0 * Math.sin(2.0 * x * PI)) * 2.0 / 3.0
  ret += (20.0 * Math.sin(y * PI) + 40.0 * Math.sin(y / 3.0 * PI)) * 2.0 / 3.0
  ret += (160.0 * Math.sin(y / 12.0 * PI) + 320.0 * Math.sin(y * PI / 30.0)) * 2.0 / 3.0
  return ret
}

function delta(lng, lat) {
  var dLng = transformLng(lng - 105.0, lat - 35.0)
  var dLat = transformLat(lng - 105.0, lat - 35.0)
  var radLat = lat / 180.0 * PI
  var magic = Math.sin(radLat)
  magic = 1 - EE * magic * magic
  var sqrtMagic = Math.sqrt(magic)
  dLng = (dLng * 180.0) / (A / sqrtMagic * Math.cos(radLat) * PI)
  dLat = (dLat * 180.0) / ((A * (1 - EE)) / (magic * sqrtMagic) * PI)
  return { lng: dLng, lat: dLat }
}

function wgs84ToGcj02(lng, lat) {
  if (outOfChina(lng, lat)) return { lng: lng, lat: lat }
  var d = delta(lng, lat)
  return { lng: lng + d.lng, lat: lat + d.lat }
}

function gcj02ToWgs84(lng, lat) {
  if (outOfChina(lng, lat)) return { lng: lng, lat: lat }
  var d = delta(lng, lat)
  return { lng: lng - d.lng, lat: lat - d.lat }
}

function gcj02ToBd09(lng, lat) {
  var z = Math.sqrt(lng * lng + lat * lat) + 0.00002 * Math.sin(lat * X_PI)
  var theta = Math.atan2(lat, lng) + 0.000003 * Math.cos(lng * X_PI)
  return { lng: z * Math.cos(theta) + 0.0065, lat: z * Math.sin(theta) + 0.006 }
}

function bd09ToGcj02(lng, lat) {
  var x = lng - 0.0065, y = lat - 0.006
  var z = Math.sqrt(x * x + y * y) - 0.00002 * Math.sin(y * X_PI)
  var theta = Math.atan2(y, x) - 0.000003 * Math.cos(x * X_PI)
  return { lng: z * Math.cos(theta), lat: z * Math.sin(theta) }
}

function wgs84ToBd09(lng, lat) {
  return gcj02ToBd09(wgs84ToGcj02(lng, lat).lng, wgs84ToGcj02(lng, lat).lat)
}

function bd09ToWgs84(lng, lat) {
  return gcj02ToWgs84(bd09ToGcj02(lng, lat).lng, bd09ToGcj02(lng, lat).lat)
}

var SYS_LABELS = { wgs84:'WGS-84', gcj02:'GCJ-02', bd09:'BD-09' }
var SYS_DESCS = { wgs84:'GPS / Google Earth', gcj02:'高德 / 腾讯地图', bd09:'百度地图' }

export default {
  name:'CoordConverterTool',
  props:{ config:{ type:Object, default:function(){ return {} } } },
  data:function(){
    return {
      tab:'single',
      lng:116.397428, lat:39.90923,
      fromSys:'wgs84', toSys:'gcj02',
      batchInput:'116.397428,39.90923\n121.473701,31.230416',
      singleResult:null, batchResult:[]
    }
  },
  computed:{
    sysLabels:function(){ return SYS_LABELS },
    sysDescs:function(){ return SYS_DESCS },
    fromLabel:function(){ return SYS_LABELS[this.fromSys] || '' },
    toLabel:function(){ return SYS_LABELS[this.toSys] || '' }
  },
  created:function(){ this.convertSingle() },
  methods:{
    convert:function(lng, lat, from, to){
      var coord = { lng:lng, lat:lat }
      if (from === to) return coord
      var key = from + '2' + to
      var map = {
        'wgs842gcj02':wgs84ToGcj02, 'gcj022wgs84':gcj02ToWgs84,
        'gcj022bd09':gcj02ToBd09, 'bd092gcj02':bd09ToGcj02,
        'wgs842bd09':wgs84ToBd09, 'bd092wgs84':bd09ToWgs84
      }
      var fn = map[key]
      return fn ? fn(lng, lat) : coord
    },
    allCoords:function(lng, lat, from){
      var pairs = {}
      var systems = ['wgs84','gcj02','bd09']
      for (var i = 0; i < systems.length; i++) {
        var c = this.convert(lng, lat, from, systems[i])
        pairs[systems[i]] = c
      }
      return pairs
    },
    convertSingle:function(){
      var lng = this.lng, lat = this.lat
      if (typeof lng !== 'number' || typeof lat !== 'number' || isNaN(lng) || isNaN(lat)) { this.singleResult = null; return }
      if (lng < -180 || lng > 180 || lat < -90 || lat > 90) { this.singleResult = null; return }
      this.singleResult = this.allCoords(lng, lat, this.fromSys)
    },
    convertBatch:function(){
      var lines = this.batchInput.trim().split('\n').filter(function(l){ return l.trim() })
      var result = []
      for (var i = 0; i < lines.length; i++) {
        var parts = lines[i].split(',')
        if (parts.length >= 2) {
          var lng = parseFloat(parts[0]), lat = parseFloat(parts[1])
          if (!isNaN(lng) && !isNaN(lat)) {
            var c = this.convert(lng, lat, this.fromSys, this.toSys)
            result.push({ fromLng:lng, fromLat:lat, toLng:c.lng, toLat:c.lat })
          }
        }
      }
      this.batchResult = result
    },
    copySingle:function(){
      if (!this.singleResult) return
      var toSys = this.toSys
      var c = this.singleResult[toSys]
      var text = c.lng.toFixed(6) + ', ' + c.lat.toFixed(6)
      navigator.clipboard.writeText(text); this.$message.success('已复制 ' + SYS_LABELS[toSys] + ' 坐标')
    },
    copyBatch:function(){
      var text = this.batchResult.map(function(r){ return r.toLng.toFixed(6) + ',' + r.toLat.toFixed(6) }).join('\n')
      navigator.clipboard.writeText(text); this.$message.success('已复制 ' + this.batchResult.length + ' 条结果')
    },
    clearBatch:function(){
      this.batchInput = ''; this.batchResult = []
    },
    doAction:function(a){
      if (a === 'clear') {
        this.lng = 116.397428; this.lat = 39.90923
        this.singleResult = null; this.batchInput = ''; this.batchResult = []
        this.convertSingle()
      }
      if (a === 'copy') {
        if (this.tab === 'single') this.copySingle()
        else this.copyBatch()
      }
    }
  }
}
</script>

<style scoped>
.cc-tool { width: 100%; }
.editor-pane { background:#fafbfc;border-radius:12px;border:1px solid #ebeef5;padding:16px 20px;margin-bottom:16px; }
.pane-header { display:flex;align-items:center;gap:8px;margin-bottom:12px; }
.pane-dot { width:8px;height:8px;border-radius:50%;flex-shrink:0; }
.input-dot { background:#4e7cff;box-shadow:0 0 6px rgba(78,124,255,.4); }
.pane-label { font-size:13px;font-weight:700;color:#1a1f36; }
.pane-hint { font-size:11px;color:#b0b8c4;margin-left:auto; }
.cc-tabs { margin-top:10px; }
.cc-form { display:grid;grid-template-columns:repeat(2,1fr);gap:12px 16px;margin-top:12px; }
.cc-field { display:flex;flex-direction:column;gap:4px; }
.cc-field label { font-size:12px;font-weight:600;color:#909399; }
.cc-batch-hint { font-size:12px;color:#909399;margin-bottom:8px; }

.cc-result { margin-bottom:16px;background:#fff;border:1px solid #ebeef5;border-radius:10px;overflow:hidden; }
.cc-result-bar { padding:10px 16px;font-size:13px;font-weight:600;color:#606266;background:#fafbfc;border-bottom:1px solid #ebeef5; }
.cc-table { width:100%;border-collapse:collapse;font-size:13px; }
.cc-table th,.cc-table td { padding:8px 12px;text-align:left;border-bottom:1px solid #ebeef5; }
.cc-table th { background:#fafbfc;color:#909399;font-weight:600;font-size:12px; }
.cc-table td { color:#303133; }
.cc-table code { font-family:monospace;font-size:13px;color:#4e7cff; }
.cc-table tr.cc-highlight td { background:#f0f4ff;font-weight:600; }
.cc-highlight .cc-tag { transform:scale(1.05); }
.cc-tag { display:inline-block;padding:1px 8px;border-radius:3px;font-size:11px;font-weight:600; }
.cc-tag-wgs84 { background:#e8f0fe;color:#4e7cff; }
.cc-tag-gcj02 { background:#e6f7e6;color:#67c23a; }
.cc-tag-bd09 { background:#fff3e0;color:#e6a23c; }
.cc-desc { font-size:12px;color:#909399; }
.cc-action { padding:8px 16px;border-top:1px solid #ebeef5; }

.cc-ref { background:#fafbfc;border:1px solid #ebeef5;border-radius:10px;padding:16px 20px; }
.cc-ref-title { font-size:13px;font-weight:700;color:#303133;margin-bottom:12px; }
.cc-ref-grid { display:flex;flex-direction:column;gap:10px; }
.cc-ref-item { display:flex;gap:12px;align-items:baseline; }
.cc-ref-name { font-size:13px;font-weight:600;color:#4e7cff;flex-shrink:0;width:70px; }
.cc-ref-text { font-size:13px;color:#606266; }

@media (max-width:640px) { .cc-form { grid-template-columns:1fr; } }
</style>
